"use client";

import type { ToolCategory } from "@bg-bgi/magnus-sdk";
import type { ToolUIMeta } from "../tool-renderer";

/**
 * Build the tool-UI registry for an MCP + progressive-disclosure connector.
 *
 * MCP connectors rewrite their tool parts to the namespaced
 * `${prefix}_${mcpToolName}` identity (see `defineMcpCapability` and ADR-057),
 * so every allowlisted tool is registered under that key to render the branded
 * connector row instead of the generic JSON fallback. Read tools are
 * categorized "research" and write tools "create" so the tool-group summary
 * phrasing ("Did some research" / "Created …") reads correctly. All entries
 * share one lazy `view`; specialize a tool later by registering a dedicated
 * view for its key.
 *
 * Registries built here are wired into the built-in registry in
 * `packages/sdk/react/src/tools/built-in-registry.ts`.
 */
export function createMcpToolRegistry({
  prefix,
  readTools,
  writeTools,
  view,
}: {
  /** Capability id used to namespace tool keys (e.g. `smartsheet`, `monday`). */
  prefix: string;
  /** Upstream read tool names (categorized "research"). */
  readTools: readonly string[];
  /** Upstream write tool names (categorized "create"). */
  writeTools: readonly string[];
  /** Shared lazy view backing every tool key. */
  view: ToolUIMeta["view"];
}): Record<string, ToolUIMeta> {
  const entry = (category: ToolCategory): ToolUIMeta => ({
    kind: "step",
    category,
    view,
  });
  return {
    ...Object.fromEntries(
      readTools.map((name) => [`${prefix}_${name}`, entry("research")])
    ),
    ...Object.fromEntries(
      writeTools.map((name) => [`${prefix}_${name}`, entry("create")])
    ),
  };
}
